import { observer } from 'mobx-react-lite'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type BundleItemViewModel } from '@/features/admin/bundle/BundleItemViewModel'
import { type WorkspaceBundleViewModel } from '@/features/admin/bundle/WorkspaceBundleViewModel'
import {
  BundleManagerCard,
  BundleManagerWorkspaceCard,
} from '@/features/admin/bundle/components/BundleManagerCard'

export interface BundleManagerCardContainerProps {
  vm: WorkspaceBundleViewModel
  workspace: Workspace
}

export const BundleManagerCardContainer = observer(
  function BundleManagerCardContainer({
    vm,
    workspace,
  }: BundleManagerCardContainerProps) {
    if (!vm.hasBundles) {
      return null
    }

    if (!vm.isFilteredByWorkspace) {
      return (
        <BundleManagerWorkspaceCardContainer
          bundleVms={vm.bundleVms}
          title={workspace.name}
          vm={vm}
        />
      )
    }

    const bundleVms = vm.bundleVmsGroupedByHavingArchivedItems
    return (
      <>
        {bundleVms.hasArchivedItems?.length > 0 && (
          <BundleManagerWorkspaceCardContainer
            bundleVms={bundleVms.hasArchivedItems}
            title="Needs attention"
            vm={vm}
          />
        )}
        {bundleVms.allActive?.length > 0 && (
          <BundleManagerWorkspaceCardContainer
            bundleVms={bundleVms.allActive}
            title={workspace.name}
            vm={vm}
          />
        )}
      </>
    )
  },
)

interface BundleManagerWorkspaceCardContainerProps {
  vm: WorkspaceBundleViewModel
  title: string
  bundleVms: BundleItemViewModel[]
}

const BundleManagerWorkspaceCardContainer = observer(
  function BundleManagerWorkspaceCardContainer({
    vm,
    title,
    bundleVms,
  }: BundleManagerWorkspaceCardContainerProps) {
    return (
      <BundleManagerWorkspaceCard
        title={title}
        isAllSelected={vm.selectedState === 'AllSelected'}
        isSomeSelected={vm.selectedState === 'SomeSelected'}
        selectedCount={vm.selectedBundlesId.size}
        onToggleAll={vm.toggleAll}
        onClearSelection={vm.clearSelectedBundle}
        showBulkActions={vm.showBulkActions}
        onBulkApplyDiscount={vm.bulkApplyDiscount}
        onBulkEditPrice={vm.bulkEditPrice}
        onBulkDuplicate={vm.bulkDuplicate}
        onBulkArchive={vm.bulkArchive}
        testId="bulk"
      >
        {bundleVms.map((bundleVm) => (
          <BundleManagerCardItemContainer
            key={bundleVm.bundle.id}
            vm={bundleVm}
          />
        ))}
      </BundleManagerWorkspaceCard>
    )
  },
)

interface BundleManagerCardItemContainerProps {
  vm: BundleItemViewModel
}

const BundleManagerCardItemContainer = observer(
  function BundleManagerCardItemContainer({
    vm,
  }: BundleManagerCardItemContainerProps) {
    const bundle = vm.bundle
    return (
      <BundleManagerCard
        id={bundle.id}
        name={bundle.name}
        description={bundle.description}
        price={bundle.price}
        workspaceIconUrl={bundle.workspace.branding.logos.icon}
        includedItemsCount={bundle.includedItems.items.length}
        isArchived={bundle.archived}
        containsArchivedItems={bundle.containsArchivedItems}
        archivedItemsCount={bundle.archivedItemsCount}
        lastEditedByName={
          bundle.lastEditedBy ? bundle.lastEditedBy.displayName : null
        }
        createdByName={bundle.createdBy.displayName}
        isSelected={vm.isSelected}
        onSelectedChange={vm.setSelected}
        onUnarchive={vm.unarchive}
        onArchive={vm.archive}
        onDuplicate={vm.duplicate}
        onEdit={vm.edit}
        testId="bundle"
      />
    )
  },
)
